/** */
import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { prepareCheckoutConfig } from './helper';
import { Button } from 'app/shared/button';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';

import { GridColumn, GridRow } from 'app/layouts/grid';
import { DetailItem } from 'app/shared/info-section/item';
import useValidator from 'hooks/validator';
import { refillWalletValidationConfig } from 'app/shared/utils/form/validation/wallet';
import { TenantContext } from 'contexts/tenant';
import { useTaxService } from 'hooks/sales/tax';
import { useCurrencyService } from 'hooks/sales/currency';
import styles from './wallet.module.css';
import { Spacer } from 'app/layouts/generic';
import { toReadableNumber } from 'app/shared/utils/number';
import { useWalletService } from 'hooks/sales/wallet';
import { usePaymentService } from 'hooks/sales/payment';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { InfoBanner } from 'app/shared/info-banner';
import { VirtualAccountContext } from 'contexts/checkout/virtual';

const credit_types = [
  { label: 'Email Credits', value: 'email' },
  { label: 'SMS Credits', value: 'sms' },
  { label: 'Push Credits', value: 'push' }
];

const TopupForm = ({ closeModal }) => {
  const wallet_in_store = useSelector((state) => state.wallet);
  const { fetchCurrencies } = useCurrencyService();
  const { createPayment } = usePaymentService();
  const { fetchSubscription } = useSubscriptionService();
  const { tenant, is_agency_managed } = useContext(TenantContext);
  const { fetchTaxes } = useTaxService();
  const { determineTopUpCostSummary, fetchWallet } = useWalletService();
  const { errors, validateField, form_is_valid } = useValidator(refillWalletValidationConfig);
  const { openModal, setPaymentDetails: setVirtualPaymentDetails } =
    useContext(VirtualAccountContext);

  const [amount, setAmount] = useState(0);
  const [credits_to_buy, setCreditsToBuy] = useState(0);
  const [credits_type, setCreditsType] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [has_subscription, setHasSubscription] = useState(false);
  const [selected_currency, setSelectedCurrency] = useState({});
  const [tax, setTax] = useState(0);
  const [taxes, setTaxes] = useState([]);
  const [tax_rate, setTaxRate] = useState(0);
  const [total_amount, setTotalAmount] = useState(0);

  useEffect(() => {
    Promise.all([
      fetchCurrencies(),
      fetchSubscription({ query_string: 'plan_code=campaign&status=active' }),
      fetchTaxes(),
      fetchWallet()
    ]).then(
      ([
        { currencies },
        {
          subscriptions: [subscription]
        },
        { taxes }
      ]) => {
        const mapped_currencies = (currencies || []).map((curr) => {
          return {
            ...curr,
            label: curr.code,
            value: curr.code
          };
        });
        const currency = mapped_currencies.find((option) => option.value === 'NGN');
        const { tax: tx_rate } = taxes.find((tax) => tax.currency === 'NGN') || { tax: 0 };

        /** */
        setCurrencies(mapped_currencies);
        setHasSubscription(subscription && subscription.tier !== 'explorer');
        setSelectedCurrency(currency);
        setTaxes(taxes || []);
        setTaxRate(tx_rate);
      }
    );
  }, []);

  useEffect(() => {
    validateField('credits_to_buy', credits_to_buy);
    validateField('credits_type', credits_type);
    validateField('currency', selected_currency);

    const { tax: tx_rate } = taxes.find((tax) => tax.currency === selected_currency.value) || {
      tax: 0
    };

    //
    const {
      amount,
      tax,
      total_amount: total
    } = determineTopUpCostSummary({
      credits_to_buy,
      currency: selected_currency.value,
      has_subscription,
      tax_rate: tx_rate,
      wallet: wallet_in_store,
      credits_type: credits_type.value
    });

    setAmount(amount);
    setTax(tax);
    setTaxRate(tx_rate);
    setTotalAmount(total);
  }, [selected_currency?.value, credits_to_buy, credits_type?.value, wallet_in_store]);

  const generateDate = (months = 1) => {
    const date = new Date();
    const end_date_timestamp = date.setMonth(date.getMonth() + months);
    const [end_date] = new Date(end_date_timestamp).toISOString().split('T');

    return end_date;
  };

  const submit = async () => {
    const int_amount = Number(total_amount);
    if (int_amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    if (!tenant || !tenant.id) {
      return toast.error('Unable to load data. Please reload browser.');
    }

    const total_cost = Number(total_amount) + Number(tax);
    const config = prepareCheckoutConfig({
      tenant,
      amount: total_cost,
      currency: selected_currency
    });
    const data = {
      amount: total_amount,
      currency: selected_currency.value,
      conversion_rate: selected_currency.exchange_rate,
      email_credits: credits_to_buy,
      expiration_timestamp: Date.parse(generateDate(12)),
      tenant_id: tenant.id,
      tx_ref: config.tx_ref,
      tx_type: credits_type.value
    };
    const result = await createPayment({ data });

    if (result) {
      if (total_amount > 400000) {
        setVirtualPaymentDetails({
          amount: total_cost,
          currency: selected_currency.value,
          payment_id: result.id,
          plan_name: `${credits_type.label}`,
          tenant,
          tx_ref: config.tx_ref
        });
        openModal();
        return;
      }
      window.FlutterwaveCheckout(config);
      closeModal();
    }
  };

  if (is_agency_managed) {
    return (
      <InfoBanner
        type="info"
        message="Credits for this organisation are managed by your agency. Please contact them to top up."
      />
    );
  }

  return (
    <>
      <div>
        <GridRow num_of_columns={4}>
          <GridColumn span={3} />
          <GridColumn>
            <DetailItem>
              <MultiSelect
                className="gm-input"
                options={currencies}
                onChange={setSelectedCurrency}
                sort_options_by="source"
                value={selected_currency}
                error={errors.currency}
              />
            </DetailItem>
          </GridColumn>
        </GridRow>
        <div className={styles.details}>
          <GridRow num_of_columns={2}>
            <GridColumn>Cost</GridColumn>
            <GridColumn>
              <div style={{ textAlign: 'right' }}>
                {selected_currency?.value} {toReadableNumber(amount)}
              </div>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={2}>
            <GridColumn>VAT({tax_rate}%)</GridColumn>
            <GridColumn>
              <div style={{ textAlign: 'right' }}>
                {selected_currency?.value} {toReadableNumber(tax)}
              </div>
            </GridColumn>
          </GridRow>
          <Spacer multiple={4} />
          <b>
            <GridRow num_of_columns={2}>
              <GridColumn>Total</GridColumn>
              <GridColumn>
                <div style={{ textAlign: 'right' }}>
                  {selected_currency?.value} {toReadableNumber(tax + total_amount)}
                </div>
              </GridColumn>
            </GridRow>
          </b>
        </div>
        <Spacer multiple={4} />
        {credits_type.value === 'sms' && (
          <GridRow num_of_columns={1}>
            <GridColumn>
              <InfoBanner message="1 SMS message costs between 8 - 11 SMS credits." />
              <Spacer multiple={4} />
            </GridColumn>
          </GridRow>
        )}
        <GridRow num_of_columns={3}>
          <GridColumn>
            <DetailItem title="Purpose">
              <MultiSelect
                options={credit_types}
                value={credits_type}
                onChange={setCreditsType}
                error={errors.credits_type}
              />
            </DetailItem>
          </GridColumn>
          <GridColumn span={2}>
            <DetailItem title="Number of credits">
              <SimpleInput
                className="gm-input"
                disabled={!credits_type.value}
                type="number"
                value={credits_to_buy}
                onInput={setCreditsToBuy}
                error={errors.credits_to_buy}
              />
            </DetailItem>
          </GridColumn>
        </GridRow>
        <GridRow num_of_columns={2}>
          <GridColumn />
          <GridColumn>
            <Button
              text={`Pay ${selected_currency?.value} ${toReadableNumber(total_amount + tax)}`}
              onClick={submit}
              disabled={!form_is_valid}
            />
          </GridColumn>
        </GridRow>
      </div>
    </>
  );
};

export default TopupForm;
